import { useState } from "react";
import dice from "../../img/dice.png";
import useRandomContent from "../../hooks/useRandomContent";

/**
 * DiceButton
 *
 * A self-contained dice button that fetches a random content suggestion and
 * passes the result back to the parent via `onContentFetched`.
 *
 * Props:
 * @param {string}   category              - Required. Content category sent to the API.
 * @param {string}   [subCategory]         - Optional. Sub-category for finer content scoping.
 * @param {string}   [lanText]             - Optional lanText override (falls back to context).
 * @param {Function} onContentFetched      - Called with the cleaned content string on success.
 * @param {boolean}  [disabled=false]      - Disables the button (e.g. while uploading).
 * @param {object}   [style]               - Extra inline styles for the outer wrapper button.
 * @param {string}   [size="md"]           - "sm" | "md" | "lg" — controls button dimensions.
 *
 * Sizes:
 *   sm → 30px  |  md → 37px (default)  |  lg → 46px
 *
 * Usage (no subCategory):
 *   <DiceButton
 *     category={category}
 *     onContentFetched={(text) => setUserContent(text)}
 *     disabled={isUploading || isCheckingConnection}
 *   />
 *
 * Usage (with subCategory):
 *   <DiceButton
 *     category="dare"
 *     subCategory="mild"
 *     onContentFetched={(text) => setUserContent(text)}
 *   />
 */

const SIZE_MAP = {
    sm: 30,
    md: 37,
    lg: 46,
};

const DiceButton = ({
    category,
    subCategory,
    lanText,
    onContentFetched,
    disabled = false,
    style = {},
    size = "md",
}) => {
    const { fetchRandomContent } = useRandomContent();
    const [isPressed, setIsPressed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dimension = SIZE_MAP[size] ?? SIZE_MAP.md;
    const imgSize = `${Math.round(dimension * 0.57)}px`;

    const handlePointerDown = async (e) => {
        e.preventDefault();
        if (disabled || isLoading) return;

        setIsPressed(true);
        setIsLoading(true);

        const content = await fetchRandomContent({ category, subCategory, lanText });
        if (content) onContentFetched?.(content);

        setIsLoading(false);
    };

    const handlePointerUp = () => setIsPressed(false);
    const handlePointerLeave = () => setIsPressed(false);

    return (
        <div>
            <button
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerLeave}
                disabled={disabled}
                className="btn btn-link p-0 position-absolute d-flex justify-content-center align-items-center"
                style={{
                    width: dimension * 2,
                    height: dimension * 2,
                    bottom: "-8px",
                    right: "-8px",
                    // border: "none",
                    cursor: disabled ? "not-allowed" : "pointer",
                    WebkitTapHighlightColor: "transparent",
                    opacity: disabled ? 0.5 : 1,
                    ...style,
                }}
                aria-label="Suggest random content"
            >
                <div
                    className="rounded-circle d-flex justify-content-center align-items-center pt-1"
                    style={{
                        width: dimension,
                        height: dimension,
                        /* Glass pill — same across all components */
                        background: "rgba(255,255,255,0.40)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        boxShadow: "inset 0 2px 2px rgba(255,255,255,0.5)",
                        transform: isPressed ? "scale(0.92)" : "scale(1)",
                        transition: "transform 0.1s ease, opacity 0.1s ease",
                    }}
                >
                    {isLoading ? (
                        /* Tiny spinner shown while fetching */
                        <div
                            style={{
                                width: imgSize,
                                height: imgSize,
                                border: "2px solid rgba(255,255,255,0.4)",
                                borderTop: "2px solid #fff",
                                borderRadius: "50%",
                                animation: "dice-spin 0.6s linear infinite",
                            }}
                        />
                    ) : (
                        <img
                            src={dice}
                            alt="dice"
                            style={{
                                width: imgSize,
                                height: imgSize,
                                opacity: isPressed ? 0.8 : 1,
                                transition: "opacity 0.1s ease",
                            }}
                        />
                    )}
                </div>

                {/* Spinner keyframe injected once */}
                <style>{`
                @keyframes dice-spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
            </button>

        </div>
    );
};

export default DiceButton;